import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
    DollarSign,
    Plus,
    Edit,
    Trash2,
    Search,
    Coins,
    Clock,
    User,
    TrendingUp,
    ShieldAlert,
    AlertTriangle
} from "lucide-react";

const CurrencyRateManagement = () => {
    const { user } = useAuth();
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [selectedRate, setSelectedRate] = useState(null);
    const [rateToDelete, setRateToDelete] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        currency_code: "",
        conversion_rate: ""
    });

    const isReadOnly = user?.role === "sales" || user?.role === "customer";

    useEffect(() => {
        if (user?.role === "customer") {
            setError("Access denied. Customers are not authorized to view currency rates.");
            setLoading(false);
            return;
        }
        loadRates();
    }, [user]);

    const loadRates = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await api.getCurrencyRates();
            setRates(response.data || []);
        } catch (err) {
            console.error("Error loading currency rates:", err);
            setError("Failed to load currency rates. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (msg, type = "success") => {
        if (type === "success") {
            setSuccessMessage(msg);
            setTimeout(() => setSuccessMessage(""), 4000);
        } else {
            setError(msg);
            setTimeout(() => setError(""), 5000);
        }
    };

    const handleCreateRate = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;
        try {
            setError("");
            await api.createCurrencyRate({
                currency_code: formData.currency_code.trim(),
                conversion_rate: parseFloat(formData.conversion_rate)
            });
            setShowCreateModal(false);
            resetForm();
            loadRates();
            showNotification("Currency rate created successfully.");
        } catch (err) {
            console.error("Error creating rate:", err);
            showNotification(
                err.response?.data?.detail || "Failed to create currency rate. Please try again.",
                "error"
            );
        }
    };

    const handleUpdateRate = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;
        try {
            setError("");
            await api.updateCurrencyRate(selectedRate.currency_code, {
                currency_code: formData.currency_code.trim(),
                conversion_rate: parseFloat(formData.conversion_rate)
            });
            setShowEditModal(false);
            setSelectedRate(null);
            resetForm();
            loadRates();
            showNotification("Currency rate updated successfully.");
        } catch (err) {
            console.error("Error updating rate:", err);
            showNotification(
                err.response?.data?.detail || "Failed to update currency rate. Please try again.",
                "error"
            );
        }
    };

    const triggerDeleteConfirm = (code) => {
        if (isReadOnly) return;
        setRateToDelete(code);
        setShowDeleteConfirmModal(true);
    };

    const executeDeleteRate = async () => {
        if (isReadOnly || !rateToDelete) return;
        try {
            setError("");
            await api.deleteCurrencyRate(rateToDelete);
            loadRates();
            showNotification(`Currency rate for ${rateToDelete} deleted successfully.`);
        } catch (err) {
            console.error("Error deleting rate:", err);
            showNotification(
                err.response?.data?.detail || "Failed to delete currency rate. Please try again.",
                "error"
            );
        } finally {
            setShowDeleteConfirmModal(false);
            setRateToDelete(null);
        }
    };

    const openEditModal = (rate) => {
        setSelectedRate(rate);
        setFormData({
            currency_code: rate.currency_code,
            conversion_rate: rate.conversion_rate.toString()
        });
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({
            currency_code: "",
            conversion_rate: ""
        });
    };

    const isOutdated = (updatedAtStr) => {
        if (!updatedAtStr) return true;
        const updatedAt = new Date(updatedAtStr);
        const now = new Date();
        const diffTime = Math.abs(now - updatedAt);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 30;
    };

    const filteredRates = rates.filter((r) =>
        r.currency_code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (user?.role === "customer") {
        return (
            <div className="flex items-center justify-center h-[70vh]">
                <div className="text-center p-8 max-w-md bg-white rounded-2xl shadow-sm border border-gray-200">
                    <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4 animate-bounce" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600">
                        You do not have permission to view or manage currency conversion rates.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-orange-50 text-orange-600 rounded-xl">
                            <Coins className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Currency Rates</h1>
                            <p className="text-sm text-gray-500">
                                View and manage conversion rates used across Traveego
                            </p>
                        </div>
                    </div>

                    {!isReadOnly && (
                        <button
                            onClick={() => {
                                resetForm();
                                setShowCreateModal(true);
                            }}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 active:scale-95 transition-all shadow-md shadow-orange-100"
                        >
                            <Plus className="w-5 h-5" />
                            Add Currency
                        </button>
                    )}
                </div>

                {/* Notifications & Warning Banners */}
                {successMessage && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl flex items-center gap-2 animate-fade-in">
                        <span className="text-lg">✓</span>
                        <p className="font-medium text-sm">{successMessage}</p>
                    </div>
                )}

                {error && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl flex items-center gap-2">
                        <span className="text-lg">⚠</span>
                        <p className="font-medium text-sm">{error}</p>
                    </div>
                )}

                {isReadOnly && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3.5 rounded-xl flex items-center gap-3">
                        <Clock className="w-5 h-5 text-amber-600 shrink-0" />
                        <div>
                            <p className="font-semibold text-sm">View-Only Mode Enabled</p>
                            <p className="text-xs text-amber-700 mt-0.5">
                                Sales role is not permitted to create, edit, or delete currency rates.
                            </p>
                        </div>
                    </div>
                )}

                {/* Filters & Actions Grid */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="relative max-w-md w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Filter by currency code (e.g. USD, EUR)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm"
                        />
                    </div>
                </div>

                {/* Rates List/Table */}
                {loading ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-orange-600 border-t-transparent"></div>
                        <p className="mt-4 text-gray-500 font-medium text-sm">Loading rates...</p>
                    </div>
                ) : filteredRates.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                        <Coins className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No currency rates found</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-left">
                                <thead className="bg-gray-50/70">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Currency Code
                                        </th>
                                        <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Conversion Rate (INR)
                                        </th>
                                        <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Last Updated
                                        </th>
                                        <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Updated By User ID
                                        </th>
                                        {!isReadOnly && (
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                                                Actions
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {filteredRates.map((rate) => {
                                        const outdated = isOutdated(rate.updated_at);
                                        return (
                                            <tr key={rate.currency_rates_id} className={`hover:bg-gray-50/50 transition-colors ${outdated ? "bg-rose-50/20" : ""}`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2.5">
                                                        <span className="font-semibold text-gray-900">
                                                            {rate.currency_code}
                                                        </span>
                                                        {outdated && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-rose-100 text-rose-700 border border-rose-200 animate-pulse">
                                                                <AlertTriangle className="w-3 h-3 text-rose-600" />
                                                                Outdated (>30d)
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5 font-semibold text-gray-800">
                                                        <TrendingUp className={`w-4 h-4 ${outdated ? "text-rose-500" : "text-emerald-500"}`} />
                                                        <span>{parseFloat(rate.conversion_rate)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <div className={`flex items-center gap-1.5 ${outdated ? "text-rose-600 font-medium" : "text-gray-500"}`}>
                                                        <Clock className={`w-4 h-4 ${outdated ? "text-rose-500" : "text-gray-400"}`} />
                                                        <span>
                                                            {rate.updated_at
                                                                ? new Date(rate.updated_at + "+00:00").toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                                                                : "Never"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center gap-1.5">
                                                        <User className="w-4 h-4 text-gray-400" />
                                                        <span>
                                                            {rate.updated_by !== null
                                                                ? `User ID: ${rate.updated_by}`
                                                                : "System / Seed"}
                                                        </span>
                                                    </div>
                                                </td>
                                                {!isReadOnly && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex items-center justify-end gap-3">
                                                            <button
                                                                onClick={() => openEditModal(rate)}
                                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Edit Conversion Rate"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => triggerDeleteConfirm(rate.currency_code)}
                                                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                                title="Delete Currency"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full overflow-hidden animate-scale-up">
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200">
                            <h2 className="text-lg font-bold text-gray-900">Add New Currency</h2>
                        </div>
                        <form onSubmit={handleCreateRate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Currency Code *
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. USD, EUR, GBP"
                                    maxLength={10}
                                    value={formData.currency_code}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            currency_code: e.target.value.toUpperCase()
                                        })
                                    }
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm font-semibold"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Conversion Rate * (1 Currency = X INR)
                                </label>
                                <input
                                    type="number"
                                    step="0.00000001"
                                    placeholder="e.g. 1.23456789"
                                    value={formData.conversion_rate}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            conversion_rate: e.target.value
                                        })
                                    }
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm font-semibold"
                                    required
                                />
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 active:scale-95 transition-all text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-700 active:scale-95 transition-all text-sm shadow-md shadow-orange-100"
                                >
                                    Create Rate
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedRate && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full overflow-hidden animate-scale-up">
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200">
                            <h2 className="text-lg font-bold text-gray-900">Edit Conversion Rate</h2>
                        </div>
                        <form onSubmit={handleUpdateRate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Currency Code (Read Only)
                                </label>
                                <input
                                    type="text"
                                    value={formData.currency_code}
                                    disabled
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-500 text-sm font-semibold cursor-not-allowed"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Conversion Rate * (1 Currency = X INR)
                                </label>
                                <input
                                    type="number"
                                    step="0.00000001"
                                    placeholder="e.g. 1.23456789"
                                    value={formData.conversion_rate}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            conversion_rate: e.target.value
                                        })
                                    }
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm font-semibold"
                                    required
                                />
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedRate(null);
                                        resetForm();
                                    }}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 active:scale-95 transition-all text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 bg-orange-600 text-white font-medium rounded-xl hover:bg-orange-700 active:scale-95 transition-all text-sm shadow-md shadow-orange-100"
                                >
                                    Update Rate
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            {showDeleteConfirmModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full overflow-hidden animate-scale-up animate-fade-in">
                        <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 border-b border-red-200 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <h2 className="text-lg font-bold text-red-950">Delete Currency Rate</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-gray-700 text-sm">
                                Are you sure you want to delete the currency rate for <strong>{rateToDelete}</strong>?
                                This rate will no longer be active in the system.
                            </p>
                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeleteConfirmModal(false);
                                        setRateToDelete(null);
                                    }}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 active:scale-95 transition-all text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={executeDeleteRate}
                                    className="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 active:scale-95 transition-all text-sm shadow-md shadow-red-100 animate-pulse"
                                >
                                    Confirm Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurrencyRateManagement;
